import { useEffect } from "react";
import { findScrollParent } from "../utils/scroll";

// Alto máximo de un índice lateral sticky, medido contra el contenedor que
// REALMENTE scrollea.
//
// Por qué existe: un max-height calculado con unidades de viewport parece
// correcto y no lo es. (Ojo al escribirlo en un comentario: Tailwind escanea
// estos archivos y una clase entre corchetes aquí adentro acaba emitida como
// CSS de verdad.)
// El scroller es el `<main>` de SidebarLayout, no la ventana, así que su
// alto visible es menor (navbar + paddings del layout) — y el índice
// termina más alto que el área visible, con sus últimos renglones fuera de
// alcance: no los alcanza el scroll de la página (el índice es sticky) ni
// el del propio índice (cree que cabe). El síntoma es un grupo entero que
// simplemente no se puede ver.
//
// Una vez pegado, el índice arranca a `top` px del borde del scroller, así
// que lo disponible es `clientHeight - top - aire` — independiente de lo
// que mida el encabezado de la página, que es la otra trampa.
// `modo`:
//   "ajustar" (default) — el índice se queda pegado y scrollea por dentro
//     cuando no cabe. Es lo que quiere un formulario largo como VenuePage:
//     el contenido es larguísimo y el índice tiene que seguir a la vista.
//   "fluir" — el índice se queda pegado SOLO mientras quepa completo; si no
//     cabe, deja de ser sticky y scrollea junto con la página. Evita las dos
//     barras compitiendo, que se ven rotas cuando el contenido de al lado es
//     corto y aun así el índice tiene su propio scroll.
export function useStickyMaxHeight(
  ref,
  { top = 16, aire = 16, minimo = 240, modo = "ajustar", deps = [] } = {}
) {
  useEffect(() => {
    const nav = ref.current;
    if (!nav) return undefined;
    const scroller = findScrollParent(nav);
    const ajustar = () => {
      const alto = (scroller?.clientHeight || window.innerHeight) - top - aire;
      if (modo === "fluir") {
        // scrollHeight con el alto natural (sin max-height) = lo que mide
        // el índice completo.
        nav.style.maxHeight = "";
        nav.style.position = nav.scrollHeight <= alto ? "sticky" : "static";
        return;
      }
      nav.style.maxHeight = `${Math.max(alto, minimo)}px`;
    };
    ajustar();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(ajustar) : null;
    if (observer && scroller) observer.observe(scroller);
    window.addEventListener("resize", ajustar);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", ajustar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, top, aire, minimo, modo, ...deps]);
}
